import { useState } from 'react';
import { getToolPartState } from '@cloudflare/ai-chat/react';
import type { UIMessage } from 'ai';

interface ToolCallCardProps {
  part: UIMessage['parts'][number];
}

/**
 * Compact card for tool invocations. Shows the tool name, a state
 * indicator (pending / streaming / complete / error), and an
 * expand-on-click view of the input + output JSON.
 *
 * The card visually de-emphasizes itself when complete so the
 * conversation reads naturally; in-flight calls glow to draw the eye to
 * what the agent is working on.
 */
export function ToolCallCard({ part }: ToolCallCardProps) {
  const [open, setOpen] = useState(false);
  const state = getToolPartState(part);
  const toolName = extractToolName(part);
  const input = (part as { input?: unknown }).input;
  const output = (part as { output?: unknown }).output;
  const error = (part as { errorText?: string }).errorText;

  const status = describeState(state);

  return (
    <div
      className={[
        'my-1 rounded-lg border text-xs transition-colors',
        state === 'error' || state === 'denied'
          ? 'border-rose-400/30 bg-rose-500/5'
          : state === 'complete'
            ? 'border-white/10 bg-white/2'
            : 'border-blue-400/30 bg-blue-500/5 shadow-[inset_0_0_0_1px] shadow-blue-400/10',
      ].join(' ')}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60"
        aria-expanded={open}
        aria-controls={`tool-body-${toolName}`}
        onClick={() => setOpen((v) => !v)}
      >
        <StateIcon state={state} />
        <span className="font-mono opacity-90" translate="no">
          {toolName}
        </span>
        <span className="opacity-60">{status}</span>
        <span aria-hidden="true" className="ms-auto opacity-50">
          {open ? '▾' : '▸'}
        </span>
      </button>
      {open && (
        <div
          id={`tool-body-${toolName}`}
          className="space-y-2 border-t border-white/8 px-3 py-2"
        >
          {input !== undefined && (
            <DetailBlock label="Input" value={input} />
          )}
          {output !== undefined && (
            <DetailBlock label="Output" value={output} />
          )}
          {error && (
            <p className="rounded border border-rose-400/30 bg-rose-500/10 p-2 text-rose-200">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function DetailBlock({ label, value }: { label: string; value: unknown }) {
  let pretty: string;
  try {
    pretty = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  } catch {
    pretty = String(value);
  }
  return (
    <div>
      <div className="mb-1 uppercase tracking-wider opacity-60">{label}</div>
      <pre className="max-h-72 overflow-auto rounded bg-black/40 px-2 py-1 text-[11px] leading-relaxed">
        {pretty}
      </pre>
    </div>
  );
}

function extractToolName(part: UIMessage['parts'][number]): string {
  // Tool parts have type `tool-${name}` per AI SDK v6; strip the prefix.
  if (typeof part.type === 'string' && part.type.startsWith('tool-')) {
    return part.type.slice('tool-'.length);
  }
  return part.type;
}

function describeState(state: ReturnType<typeof getToolPartState>): string {
  switch (state) {
    case 'loading':
      return '· dispatching…';
    case 'streaming':
      return '· running…';
    case 'waiting-approval':
      return '· awaiting approval';
    case 'approved':
      return '· approved';
    case 'denied':
      return '· denied';
    case 'error':
      return '· error';
    case 'complete':
      return '· done';
    default:
      return '';
  }
}

function StateIcon({ state }: { state: ReturnType<typeof getToolPartState> }) {
  if (state === 'loading' || state === 'streaming') {
    return (
      <span
        aria-hidden="true"
        className="h-2 w-2 animate-pulse rounded-full bg-blue-400"
      />
    );
  }
  if (state === 'error' || state === 'denied') {
    return (
      <span aria-hidden="true" className="h-2 w-2 rounded-full bg-rose-400" />
    );
  }
  return (
    <span aria-hidden="true" className="h-2 w-2 rounded-full bg-emerald-400" />
  );
}
